import { useEffect } from 'react';

/**
 * Ports the original main.js motion layer into a single effect.
 * Runs once after the page mounts, wiring up every scroll/reveal animation
 * against the rendered DOM (class-name driven, matching the source design).
 */
export default function useSiteMotion() {
  useEffect(() => {
    const $ = (sel, el = document) => el.querySelector(sel);
    const $$ = (sel, el = document) => Array.from(el.querySelectorAll(sel));

    const cleanups = [];
    const timers = [];
    const on = (target, type, handler, opts) => {
      target.addEventListener(type, handler, opts);
      cleanups.push(() => target.removeEventListener(type, handler, opts));
    };

    /* ── NAV scroll state ── */
    const nav = $('.nav');
    if (nav) {
      const onScrollNav = () => {
        if (window.scrollY > 30) nav.classList.add('scrolled');
        else nav.classList.remove('scrolled');
      };
      on(window, 'scroll', onScrollNav, { passive: true });
      onScrollNav();
    }

    /* ── HERO load reveal (staggered) ── */
    const heroTitle = $('.hero-title');
    window.requestAnimationFrame(() => {
      $$('.hero-title .reveal-w').forEach((w, i) => {
        timers.push(setTimeout(() => w.classList.add('in'), 120 + i * 90));
      });
      timers.push(setTimeout(() => heroTitle && heroTitle.classList.add('in'), 200));
      $$('.hero .reveal').forEach((el, i) => {
        timers.push(setTimeout(() => el.classList.add('in'), 600 + i * 140));
      });
    });

    /* ── Count-up stats ── */
    const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);
    const runCount = (el) => {
      const to = parseFloat(el.getAttribute('data-to'));
      const dec = parseInt(el.getAttribute('data-dec') || '0', 10);
      const dur = 1500;
      const start = performance.now();
      const tick = (now) => {
        const p = Math.min(1, (now - start) / dur);
        const v = to * easeOutQuart(p);
        el.textContent = v.toFixed(dec);
        if (p < 1) requestAnimationFrame(tick);
        else el.textContent = to.toFixed(dec);
      };
      requestAnimationFrame(tick);
    };
    $$('.hero-foot .cv').forEach((el) =>
      timers.push(setTimeout(() => runCount(el), 1100))
    );

    const easeInOut = (t) =>
      t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

    /* ── Analytics donut: ring + number sweep in sync, looping while in view ──
       5% → 68% (ease-out), hold, ease back to 5%, hold, repeat. A single rAF
       drives both --p and the label so they can never desync. */
    const donut = $('.viz-donut');
    if (donut) {
      const donutNum = $('.vd-num', donut);
      const FROM = 5;
      const TO = 68;
      const UP = 1600;
      const HOLD_TOP = 1500;
      const DOWN = 800;
      const HOLD_BOT = 500;
      const CYCLE = UP + HOLD_TOP + DOWN + HOLD_BOT;
      const apply = (v) => {
        donut.style.setProperty('--p', (v / 100).toFixed(4));
        if (donutNum) donutNum.textContent = `+${Math.round(v)}%`;
      };
      const valueAt = (e) => {
        let m = ((e % CYCLE) + CYCLE) % CYCLE;
        if (m < UP) return FROM + (TO - FROM) * easeOutQuart(m / UP);
        m -= UP;
        if (m < HOLD_TOP) return TO;
        m -= HOLD_TOP;
        if (m < DOWN) return TO + (FROM - TO) * easeInOut(m / DOWN);
        return FROM;
      };
      let donutRaf = 0;
      let donutT0 = 0;
      const donutTick = (now) => {
        apply(valueAt(now - donutT0));
        donutRaf = requestAnimationFrame(donutTick);
      };
      const startDonut = () => {
        if (donutRaf) return;
        donutT0 = performance.now();
        donutRaf = requestAnimationFrame(donutTick);
      };
      const stopDonut = () => {
        if (donutRaf) cancelAnimationFrame(donutRaf);
        donutRaf = 0;
      };
      const donutIO = new IntersectionObserver(
        (entries) =>
          entries.forEach((e) => (e.isIntersecting ? startDonut() : stopDonut())),
        { threshold: 0.4 }
      );
      donutIO.observe(donut);
      cleanups.push(() => {
        donutIO.disconnect();
        stopDonut();
      });
    }

    /* ── Live-tracking stepper: fill line + nodes light in lockstep, looping ──
       A single rAF sweeps the fill 0→1 and lights each node exactly when the
       fill reaches it, so every point changes colour in sync (not just the
       last). Holds full, empties, repeats — only runs while in view. */
    const steps = $('.viz-steps');
    if (steps) {
      const stepFill = $('.vs-fill', steps);
      const stepNodes = $$('.vs-nodes span', steps);
      const N = stepNodes.length;
      // Step-by-step: line grows to each node one at a time
      const SEG_DUR  = 480;   // ms: line travels to the next node
      const PAUSE_MS = 750;   // ms: hold at each node before moving on
      const HOLD_MS  = 1600;  // ms: hold at Served before resetting
      const RESET_MS = 500;   // ms: blank pause before next cycle
      const PERIOD   = SEG_DUR + PAUSE_MS;
      const CYCLE    = (N - 1) * PERIOD + HOLD_MS + RESET_MS;
      const fracAt   = (i) => (N <= 1 ? 1 : i / (N - 1));

      const setProg = (p) => {
        if (stepFill) stepFill.style.transform = `scaleX(${p.toFixed(4)})`;
        stepNodes.forEach((n, i) =>
          n.classList.toggle('lit', p >= fracAt(i) - 0.001)
        );
      };

      // Returns fill progress (0–1) for a given elapsed time within the cycle
      const progAt = (elapsed) => {
        const e = ((elapsed % CYCLE) + CYCLE) % CYCLE;
        for (let i = 1; i < N; i++) {
          const segStart  = (i - 1) * PERIOD;
          const segEnd    = segStart + SEG_DUR;
          const pauseEnd  = segStart + PERIOD;
          if (e <= segEnd) {
            // Line growing from node i-1 → node i
            const t = (e - segStart) / SEG_DUR;
            return fracAt(i - 1) + (fracAt(i) - fracAt(i - 1)) * easeOutQuart(t);
          }
          if (e < pauseEnd) return fracAt(i); // holding at node i
        }
        // Hold at Served
        if (e < (N - 1) * PERIOD + HOLD_MS) return 1;
        // Reset gap — return 0 so all nodes unlight before next cycle
        return 0;
      };
      let stepRaf = 0;
      let stepT0 = 0;
      const stepTick = (now) => {
        setProg(progAt(now - stepT0));
        stepRaf = requestAnimationFrame(stepTick);
      };
      const startStep = () => {
        if (stepRaf) return;
        stepT0 = performance.now();
        stepRaf = requestAnimationFrame(stepTick);
      };
      const stopStep = () => {
        if (stepRaf) cancelAnimationFrame(stepRaf);
        stepRaf = 0;
      };
      const stepIO = new IntersectionObserver(
        (entries) =>
          entries.forEach((e) => (e.isIntersecting ? startStep() : stopStep())),
        { threshold: 0.3 }
      );
      stepIO.observe(steps);
      cleanups.push(() => {
        stepIO.disconnect();
        stopStep();
      });
    }

    /* ── Generic reveal on scroll + reveal-words splitting ── */
    const revealEls = $$('.reveal').filter((el) => !el.closest('.hero'));

    $$('.reveal-words').forEach((h) => {
      const wrapTextNodes = (node) => {
        Array.from(node.childNodes).forEach((child) => {
          if (child.nodeType === 3) {
            const text = child.textContent;
            if (!text.trim()) return;
            const frag = document.createDocumentFragment();
            text.split(/(\s+)/).forEach((token) => {
              if (!token) return;
              if (/\s/.test(token)) {
                frag.appendChild(document.createTextNode(token));
              } else {
                const span = document.createElement('span');
                span.className = 'w-anim';
                span.textContent = token;
                frag.appendChild(span);
              }
            });
            child.replaceWith(frag);
          } else if (child.nodeType === 1) {
            if (child.classList && child.classList.contains('ai-rotator')) return;
            wrapTextNodes(child);
          }
        });
      };
      wrapTextNodes(h);
    });

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target;
            el.classList.add('in');
            $$('.w-anim', el).forEach((w, i) => {
              w.style.transitionDelay = `${i * 70}ms`;
            });
            io.unobserve(el);
          }
        });
      },
      { threshold: 0.18, rootMargin: '0px 0px -8% 0px' }
    );
    revealEls.forEach((el) => io.observe(el));
    $$('.reveal-words').forEach((el) => io.observe(el));
    cleanups.push(() => io.disconnect());

    /* ── WALKTHROUGH — carousel logic lives in Walkthrough.jsx ── */

    /* ── BENTO reveal-tile ── */
    const bentoCards = $$('.reveal-tile');
    if (bentoCards.length) {
      const tileIO = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('in');
              tileIO.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.05, rootMargin: '0px 0px 0px 0px' }
      );
      bentoCards.forEach((c) => tileIO.observe(c));
      cleanups.push(() => tileIO.disconnect());
    }

    /* ── PHONE parallax tilt ──
       Scroll-linked, so it must map 1:1 to scroll position with no CSS
       transition fighting it (see .phone in sections.css). rAF-throttled so we
       read layout + write transforms at most once per frame, not once per
       (possibly many) scroll events — keeps the main thread free for everything
       else animating during the same scroll. */
    const phones = $('.mobile-phones');
    if (phones) {
      const back = $('.phone-back', phones);
      const front = $('.phone-front', phones);
      const applyPhones = () => {
        const rect = phones.getBoundingClientRect();
        const center = window.innerHeight / 2;
        const offset = (rect.top + rect.height / 2 - center) / window.innerHeight;
        const t = Math.max(-0.7, Math.min(0.7, offset));
        if (back)
          back.style.transform = `translate(${-20 + t * 4}%, ${-10 - t * 8}%) rotate(${-7 + t * 2}deg)`;
        if (front)
          front.style.transform = `translate(${18 - t * 4}%, ${10 + t * 8}%) rotate(${5 - t * 2}deg)`;
      };
      let phoneTicking = false;
      const onScrollPhones = () => {
        if (!phoneTicking) {
          window.requestAnimationFrame(() => {
            applyPhones();
            phoneTicking = false;
          });
          phoneTicking = true;
        }
      };
      on(window, 'scroll', onScrollPhones, { passive: true });
      applyPhones();
    }

    /* ── Footer wordmark grow-on-scroll ──
       The word fills the viewport width (scaleX), and its height grows (scaleY)
       as the pinned section is scrolled, until it fills the screen. */
    const growSection = $('.foot-grow');
    if (growSection) {
      const word = $('.foot-wordmark', growSection);
      const clampN = (v, a, b) => Math.max(a, Math.min(b, v));
      let sx = 1;
      let syStart = 1;
      let syEnd = 1;
      const measure = () => {
        word.style.setProperty('--sx', '1');
        word.style.setProperty('--sy', '1');
        const r = word.getBoundingClientRect();
        if (!r.width || !r.height) return;
        sx = window.innerWidth / r.width; // fill the full viewport width
        syStart = 0; // start at 0px height
        syEnd = window.innerHeight / r.height; // grow to fill the viewport height
        applyGrow();
      };
      const applyGrow = () => {
        const rect = growSection.getBoundingClientRect();
        const scrollable = growSection.offsetHeight - window.innerHeight;
        const p =
          scrollable > 0
            ? clampN(-rect.top / scrollable, 0, 1)
            : rect.top <= 0
            ? 1
            : 0;
        word.style.setProperty('--sx', sx.toFixed(4));
        word.style.setProperty('--sy', (syStart + (syEnd - syStart) * p).toFixed(4));
      };
      let growTicking = false;
      const onGrowScroll = () => {
        if (!growTicking) {
          requestAnimationFrame(() => {
            applyGrow();
            growTicking = false;
          });
          growTicking = true;
        }
      };
      on(window, 'scroll', onGrowScroll, { passive: true });
      on(window, 'resize', measure, { passive: true });
      // Re-measure once the display font has loaded (it changes the text width).
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(measure);
      }
      measure();
    }

    /* ── Smooth-scroll anchors with fixed-nav offset ──
       Uses a custom rAF easing so the scroll stays smooth even when the browser
       maps native smooth-behaviour to an instant jump (e.g. when busy). */
    let scrollRaf = 0;
    const cancelSmooth = () => {
      if (scrollRaf) cancelAnimationFrame(scrollRaf);
      scrollRaf = 0;
    };
    const easeInOutCubic = (t) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    const smoothScrollTo = (destY) => {
      cancelSmooth();
      const startY = window.scrollY;
      const dist = destY - startY;
      if (Math.abs(dist) < 2) return;
      const dur = Math.min(850, Math.max(320, Math.abs(dist) * 0.4));
      let t0 = 0;
      const step = (now) => {
        if (!t0) t0 = now;
        const p = Math.min(1, (now - t0) / dur);
        window.scrollTo(0, Math.round(startY + dist * easeInOutCubic(p)));
        scrollRaf = p < 1 ? requestAnimationFrame(step) : 0;
      };
      scrollRaf = requestAnimationFrame(step);
    };
    // Let the user interrupt an in-progress smooth scroll.
    ['wheel', 'touchstart', 'keydown'].forEach((ev) =>
      on(window, ev, cancelSmooth, { passive: true })
    );
    cleanups.push(cancelSmooth);


$$('a[href^="#"]').forEach((a) => {
      const handler = (e) => {
        const href = a.getAttribute('href');
        if (href === '#' || href.length < 2) return;
        const target = document.querySelector(href);
        if (!target) return;
        e.preventDefault();
        const destY = Math.max(
          0,
          target.getBoundingClientRect().top + window.scrollY - 60
        );
        smoothScrollTo(destY);
      };
      on(a, 'click', handler);
    });

    return () => {
      cleanups.forEach((fn) => fn());
      timers.forEach((t) => clearTimeout(t));
    };
  }, []);
}
